import React, { useRef, useState } from "react";
import { db, storage } from "../config/firebaseConfig";
import { IMMICH_BASE_URL } from "../config/immichConfig";
import { collection, addDoc, Timestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

// Immich/Immuch server configuration (frontend safe values only).
const IMMICH_UPLOAD_URL = `${IMMICH_BASE_URL}/assets`;
const normalizeImmichApiKey = (raw) =>
  String(raw || "")
    .replace(/[\r\n]/g, "")
    .trim()
    .replace(/^["']+|["']+$/g, "");
const IMMICH_API_KEY = normalizeImmichApiKey(
  process.env.REACT_APP_IMMICH_API_KEY,
);
const IMMICH_UPLOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_IMMICH_UPLOAD_TIMEOUT_MS || 180000,
);
const IMMICH_ASSET_VERIFY_DELAY_MS = Number(
  process.env.REACT_APP_IMMICH_ASSET_VERIFY_DELAY_MS || 90000,
);
const IMMICH_ASSET_FETCH_TIMEOUT_MS = Number(
  process.env.REACT_APP_IMMICH_ASSET_FETCH_TIMEOUT_MS || 60000,
);
const IMMICH_LOG_CHUNK_SIZE = Number(
  process.env.REACT_APP_IMMICH_LOG_CHUNK_SIZE || 1600,
);
const IMMICH_DEVICE_LABEL =
  process.env.REACT_APP_IMMICH_DEVICE_ID || "CCTV_GATE_1";

const getMaskedKeyPreview = (value) => {
  const source = String(value || "");
  if (!source) return "missing";
  const start = source.slice(0, 4);
  const end = source.slice(-4);
  return `${start}...${end}`;
};

const getImmichAuthHeaders = () => ({
  Accept: "application/json",
  "x-api-key": normalizeImmichApiKey(IMMICH_API_KEY),
});

// Device asset ID should be unique per uploaded file.
const buildDeviceAssetId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `asset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractResponsePayload = async (response) => {
  const textBody = await response.text();
  let jsonBody = null;
  try {
    jsonBody = textBody ? JSON.parse(textBody) : null;
  } catch {
    jsonBody = null;
  }
  return { textBody, jsonBody };
};

const trimForLog = (value, max = 220) => {
  const source = String(value || "");
  if (source.length <= max) return source;
  return `${source.slice(0, max)}...`;
};

const formatJsonForLog = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || "");
  }
};

const chunkLogText = (value, size = 1600) => {
  const source = String(value || "");
  if (!source) return [];

  const chunks = [];
  for (let i = 0; i < source.length; i += size) {
    chunks.push(source.slice(i, i + size));
  }
  return chunks;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 45000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

const getImmichNetworkError = (error) => {
  if (error?.name === "AbortError") {
    return `Request timed out after ${IMMICH_UPLOAD_TIMEOUT_MS}ms`;
  }

  const raw = String(error?.message || "");
  if (raw.toLowerCase().includes("failed to fetch")) {
    return "Network/CORS/Proxy issue (Failed to fetch)";
  }

  return raw || "Unknown network error";
};

const buildImmichUploadPayload = (file) => {
  const fileTimestamp = new Date(file.lastModified || Date.now()).toISOString();
  const formData = new FormData();

  // Required multipart fields for POST /api/assets (uploadAsset).
  formData.append("deviceAssetId", buildDeviceAssetId());
  formData.append("deviceId", IMMICH_DEVICE_LABEL);
  formData.append("fileCreatedAt", fileTimestamp);
  formData.append("fileModifiedAt", fileTimestamp);
  formData.append("assetData", file, file.name);

  return formData;
};

function AddEventForm() {
  const [eventName, setEventName] = useState("");
  const [date, setDate] = useState("");
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("wip");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [, setUploadLogs] = useState({
    firebase: [],
    immich: [],
  });
  const [previews, setPreviews] = useState([]);
  const [fileType, setFileType] = useState("all");
  const fileInputRef = useRef();

  const statusOptions = [
    { value: "wip", label: "WIP" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const addUploadLog = (service, fileName, uploadState, message) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fileName,
      status: uploadState,
      message,
      time: new Date().toLocaleTimeString(),
    };

    setUploadLogs((prev) => ({
      ...prev,
      [service]: [...prev[service], entry],
    }));
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
    setFiles([]);
    setPreviews([]);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const addFiles = (newFiles) => {
    const existing = files.map((f) => f.name + f.size);
    const filtered = newFiles.filter(
      (f) => !existing.includes(f.name + f.size),
    );
    const updatedFiles = [...files, ...filtered];
    setFiles(updatedFiles);

    const updatedPreviews = [
      ...previews,
      ...filtered.map((file) =>
        file.type.startsWith("image/") || file.type.startsWith("video/")
          ? URL.createObjectURL(file)
          : null,
      ),
    ];
    setPreviews(updatedPreviews);
  };

  const handleRemoveFile = (idx) => {
    const updatedFiles = files.filter((_, i) => i !== idx);
    setFiles(updatedFiles);
    setPreviews(previews.filter((_, i) => i !== idx));
  };

  const uploadFilesWithProgress = async () => {
    const urls = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      addUploadLog("firebase", file.name, "uploading", "Uploading to Firebase");
      const fileRef = ref(storage, `events/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(fileRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            );
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: percent,
            }));
          },
          (error) => {
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: "error",
            }));
            addUploadLog("firebase", file.name, "error", error.message);
            reject(error);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            urls.push(url);
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: 100,
            }));
            addUploadLog(
              "firebase",
              file.name,
              "success",
              "Uploaded to Firebase",
            );
            resolve();
          },
        );
      });
    }

    return urls;
  };

  const uploadFilesToImmichInBackground = (filesToUpload) => {
    if (!filesToUpload.length) {
      return Promise.resolve({ successes: [], errors: [] });
    }

    addUploadLog(
      "immich",
      "Flow",
      "info",
      `API key present: ${Boolean(IMMICH_API_KEY)} | length: ${IMMICH_API_KEY.length} | masked: ${getMaskedKeyPreview(
        IMMICH_API_KEY,
      )}`,
    );

    if (!IMMICH_API_KEY) {
      addUploadLog(
        "immich",
        "All files",
        "error",
        "Missing REACT_APP_IMMICH_API_KEY",
      );
      return Promise.resolve({
        successes: [],
        errors: [
          {
            fileName: "All files",
            message: "Missing REACT_APP_IMMICH_API_KEY",
          },
        ],
      });
    }

    addUploadLog(
      "immich",
      "Flow",
      "info",
      `Using endpoint: POST ${IMMICH_UPLOAD_URL} (operation: uploadAsset)`,
    );
    addUploadLog("immich", "Flow", "info", `Device ID: ${IMMICH_DEVICE_LABEL}`);
    addUploadLog(
      "immich",
      "Flow",
      "info",
      `Upload timeout: ${IMMICH_UPLOAD_TIMEOUT_MS}ms`,
    );
    addUploadLog(
      "immich",
      "Flow",
      "info",
      `Asset verify delay: ${IMMICH_ASSET_VERIFY_DELAY_MS}ms`,
    );
    addUploadLog(
      "immich",
      "Flow",
      "info",
      `Asset fetch timeout: ${IMMICH_ASSET_FETCH_TIMEOUT_MS}ms`,
    );

    // Run all Immuch uploads in parallel and return a normalized result for
    // Firestore sync (`successes` + `errors`) regardless of individual failures.
    return Promise.allSettled(
      filesToUpload.map(async (file) => {
        addUploadLog(
          "immich",
          file.name,
          "uploading",
          "Step 1/2: Uploading asset to Immuch server",
        );

        try {
          const uploadRes = await fetchWithTimeout(
            IMMICH_UPLOAD_URL,
            {
              method: "POST",
              headers: getImmichAuthHeaders(),
              body: buildImmichUploadPayload(file),
            },
            IMMICH_UPLOAD_TIMEOUT_MS,
          );

          addUploadLog(
            "immich",
            file.name,
            "info",
            `Step 1/2: Response ${uploadRes.status} ${uploadRes.statusText} (${uploadRes.url})`,
          );

          const { textBody, jsonBody } =
            await extractResponsePayload(uploadRes);
          const responseContentType =
            uploadRes.headers.get("content-type") || "unknown";
          addUploadLog(
            "immich",
            file.name,
            "info",
            `Step 1/2: content-type=${responseContentType}`,
          );

          if (!uploadRes.ok) {
            throw new Error(
              `Upload failed (${uploadRes.status}): ${trimForLog(textBody)}`,
            );
          }

          if (!jsonBody && /<html|<!doctype/i.test(String(textBody || ""))) {
            // Helpful when dev proxy points to web app instead of /api.
            throw new Error(
              `Received HTML instead of Immich JSON response. url=${uploadRes.url}, body=${trimForLog(
                textBody,
              )}`,
            );
          }

          const uploadData = jsonBody || {};
          const assetId =
            uploadData?.id || uploadData?.assetId || uploadData?.asset?.id;
          const uploadStatus = uploadData?.status || "unknown";

          if (!assetId) {
            throw new Error(
              `No asset id in response. content-type=${responseContentType}, body=${trimForLog(
                textBody || JSON.stringify(uploadData),
              )}`,
            );
          }

          addUploadLog(
            "immich",
            file.name,
            "success",
            `Uploaded to Immuch. assetId=${assetId}, status=${uploadStatus}`,
          );

          addUploadLog(
            "immich",
            file.name,
            "uploading",
            `Step 2/2: Waiting ${Math.round(
              IMMICH_ASSET_VERIFY_DELAY_MS / 1000,
            )}s before fetching asset details`,
          );
          // Optional verification fetch to confirm the asset is queryable later.
          await sleep(IMMICH_ASSET_VERIFY_DELAY_MS);

          const assetDetailsUrl = `${IMMICH_BASE_URL}/assets/${assetId}`;
          addUploadLog(
            "immich",
            file.name,
            "uploading",
            `Step 2/2: Fetching asset details ${assetDetailsUrl}`,
          );

          let assetRes;
          try {
            assetRes = await fetchWithTimeout(
              assetDetailsUrl,
              {
                method: "GET",
                headers: getImmichAuthHeaders(),
              },
              IMMICH_ASSET_FETCH_TIMEOUT_MS,
            );
          } catch (error) {
            if (error?.name === "AbortError") {
              throw new Error(
                `Asset fetch timed out after ${IMMICH_ASSET_FETCH_TIMEOUT_MS}ms`,
              );
            }
            throw error;
          }

          addUploadLog(
            "immich",
            file.name,
            "info",
            `Step 2/2: Asset response ${assetRes.status} ${assetRes.statusText} (${assetRes.url})`,
          );

          const { textBody: assetBodyText, jsonBody: assetBodyJson } =
            await extractResponsePayload(assetRes);
          const assetContentType =
            assetRes.headers.get("content-type") || "unknown";
          addUploadLog(
            "immich",
            file.name,
            "info",
            `Step 2/2: content-type=${assetContentType}`,
          );

          const responseHeaders = Object.fromEntries(
            assetRes.headers.entries(),
          );
          addUploadLog(
            "immich",
            file.name,
            "info",
            `Step 2/2: headers\n${formatJsonForLog(responseHeaders)}`,
          );

          if (assetBodyJson) {
            const prettyJson = formatJsonForLog(assetBodyJson);
            const chunks = chunkLogText(prettyJson, IMMICH_LOG_CHUNK_SIZE);
            chunks.forEach((chunk, index) => {
              addUploadLog(
                "immich",
                file.name,
                "info",
                `Step 2/2: asset payload ${index + 1}/${chunks.length}\n${chunk}`,
              );
            });
          } else if (assetBodyText) {
            const chunks = chunkLogText(assetBodyText, IMMICH_LOG_CHUNK_SIZE);
            chunks.forEach((chunk, index) => {
              addUploadLog(
                "immich",
                file.name,
                "info",
                `Step 2/2: asset text payload ${index + 1}/${chunks.length}\n${chunk}`,
              );
            });
          }

          if (!assetRes.ok) {
            throw new Error(
              `Asset fetch failed (${assetRes.status}): ${trimForLog(assetBodyText)}`,
            );
          }

          if (
            !assetBodyJson &&
            /<html|<!doctype/i.test(String(assetBodyText || ""))
          ) {
            throw new Error(
              `Asset fetch returned HTML, expected JSON. body=${trimForLog(
                assetBodyText,
              )}`,
            );
          }

          const fetchedAssetId =
            assetBodyJson?.id || assetBodyJson?.assetId || assetId;
          const fetchedType =
            assetBodyJson?.type || assetBodyJson?.originalMimeType || "unknown";
          const fetchedName =
            assetBodyJson?.originalFileName ||
            assetBodyJson?.fileName ||
            file.name;
          addUploadLog(
            "immich",
            file.name,
            "success",
            `Step 2/2: Asset fetched. id=${fetchedAssetId}, file=${fetchedName}, type=${fetchedType}`,
          );

          return {
            fileName: file.name,
            assetId,
            status: uploadStatus,
            fetchedAssetId,
          };
        } catch (error) {
          const message = getImmichNetworkError(error) || error.message;
          addUploadLog("immich", file.name, "error", message);
          throw new Error(message);
        }
      }),
    )
      .then((results) => {
        const successes = [];
        const errors = [];

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            successes.push(result.value);
            return;
          }

          const message =
            getImmichNetworkError(result.reason) ||
            result.reason?.message ||
            "Unknown Immuch error";
          errors.push({
            fileName: filesToUpload[index]?.name || "Unknown file",
            message,
          });
        });

        const successCount = successes.length;
        const errorCount = results.length - successCount;
        addUploadLog(
          "immich",
          "Flow",
          errorCount > 0 ? "error" : "success",
          `Immuch summary -> success: ${successCount}, error: ${errorCount}`,
        );
        return { successes, errors };
      })
      .catch((err) => {
        const message = getImmichNetworkError(err) || err.message;
        addUploadLog(
          "immich",
          "Flow",
          "error",
          `Immuch batch error: ${message}`,
        );
        console.error("Immuch error:", err);
        return {
          successes: [],
          errors: [{ fileName: "Flow", message }],
        };
      });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventName || !date || files.length === 0) {
      alert("Please fill all fields and select at least one file");
      return;
    }

    setLoading(true);
    setUploadProgress({});
    setUploadLogs({ firebase: [], immich: [] });

    try {
      const filesSnapshot = [...files];
      // Start Immuch sync immediately, but do not block Firebase event creation.
      const immichSyncPromise = uploadFilesToImmichInBackground(filesSnapshot);
      const immichEnabled = Boolean(IMMICH_API_KEY && filesSnapshot.length > 0);

      const fileUrls = await uploadFilesWithProgress();

      // Primary save: always create Firebase event first.
      const eventRef = await addDoc(collection(db, "events"), {
        eventName,
        date,
        createdAt: Timestamp.now(),
        description,
        status,
        imageUrls: fileUrls,
        immichAssetIds: [],
        immichAssets: [],
        immichSyncStatus: immichEnabled ? "in_progress" : "skipped",
      });

      // Secondary save: patch the same event doc with Immuch IDs/results.
      immichSyncPromise
        .then(async ({ successes, errors }) => {
          const syncedAssets = successes
            .map((item) => ({
              fileName: item?.fileName || "",
              assetId: item?.assetId || item?.fetchedAssetId || "",
              fetchedAssetId: item?.fetchedAssetId || item?.assetId || "",
              status: item?.status || "unknown",
            }))
            .filter((item) => item.assetId);

          const immichAssetIds = [
            ...new Set(syncedAssets.map((item) => item.assetId)),
          ];
          const hasErrors = errors.length > 0;
          let syncStatus = "completed";

          if (!immichEnabled) syncStatus = "skipped";
          else if (hasErrors && immichAssetIds.length > 0)
            syncStatus = "partial";
          else if (hasErrors) syncStatus = "failed";

          const updatePayload = {
            immichAssetIds,
            immichAssets: syncedAssets,
            immichSyncStatus: syncStatus,
            immichSyncedAt: Timestamp.now(),
          };

          if (hasErrors) {
            updatePayload.immichErrors = errors.map((item) => ({
              fileName: item.fileName,
              message: item.message,
            }));
          }

          await updateDoc(eventRef, updatePayload);
          addUploadLog(
            "immich",
            "Flow",
            "info",
            `Firebase updated with ${immichAssetIds.length} Immuch assetId(s). eventId=${eventRef.id}`,
          );
        })
        .catch(async (immichError) => {
          const message =
            getImmichNetworkError(immichError) || immichError.message;
          addUploadLog(
            "immich",
            "Flow",
            "error",
            `Failed to update Firebase with Immuch asset IDs: ${message}`,
          );
          try {
            // Keep failure details in Firebase for later debugging/audit.
            await updateDoc(eventRef, {
              immichSyncStatus: "failed",
              immichSyncError: message,
              immichSyncedAt: Timestamp.now(),
            });
          } catch (firebaseUpdateError) {
            console.error(
              "Failed to persist Immuch failure details in Firebase:",
              firebaseUpdateError,
            );
          }
        });

      alert("Event added successfully!");
      setEventName("");
      setDescription("");
      setDate("");
      setFiles([]);
      setPreviews([]);
      setUploadProgress({});
      setStatus("wip");
    } catch (error) {
      console.error("Error adding event:", error);
      alert("Error adding event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  let acceptAttr = "";

  if (fileType === "images") acceptAttr = "image/*";
  else if (fileType === "videos") acceptAttr = "video/*";
  else if (fileType === "docs") {
    acceptAttr = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt";
  } else {
    acceptAttr = "";
  }

  const totalUploads = files.length;
  const completedUploads = files.filter(
    (file) => uploadProgress[file.name] === 100,
  ).length;
  const failedUploads = files.filter(
    (file) => uploadProgress[file.name] === "error",
  ).length;

  return (
    <div
      className="relative min-h-screen overflow-hidden px-4 py-8 sm:px-6 lg:px-10"
      style={{
        background:
          "radial-gradient(circle at 10% 0%, #1d4ed8 0%, #0f172a 35%, #020617 100%)",
        fontFamily: '"Sora", "Poppins", "Segoe UI", sans-serif',
      }}
    >
      <div className="pointer-events-none absolute -left-16 -top-28 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-20 h-80 w-80 rounded-full bg-indigo-500/20 blur-3xl" />

      <form onSubmit={handleSubmit} className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-6">
          <p className="inline-block rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-cyan-200">
            Event Studio
          </p>
          <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
            Create a New School Event
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-200">
            Upload once, publish everywhere. Firebase remains your primary
            source and Immich sync runs in the background.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <section className="rounded-3xl border border-white/20 bg-white/95 p-5 shadow-2xl backdrop-blur-lg sm:p-7 lg:col-span-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Event Name
                </label>
                <input
                  type="text"
                  placeholder="Annual Sports Meet 2026"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Event Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  File Type
                </label>
                <select
                  value={fileType}
                  onChange={handleFileTypeChange}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200"
                >
                  <option value="all">All Files</option>
                  <option value="images">Images Only</option>
                  <option value="videos">Videos Only</option>
                  <option value="docs">Documents Only</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Description
                </label>
                <textarea
                  placeholder="Write event highlights, activities, and outcomes."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Workflow Status
                </label>
                <div className="grid grid-cols-3 gap-2 rounded-2xl bg-slate-100 p-1">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStatus(option.value)}
                      className={`rounded-xl px-2 py-2 text-xs font-semibold uppercase tracking-wider transition ${
                        status === option.value
                          ? "bg-slate-900 text-white shadow-lg"
                          : "text-slate-500 hover:bg-white"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="mt-5 rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/70 p-5 text-center transition hover:border-blue-400 hover:bg-blue-50"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              title="Drag and drop files here or click to select"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptAttr}
                onChange={handleFileChange}
                className="hidden"
              />
              <p className="text-sm font-semibold text-blue-900">
                Drag files here or click to browse
              </p>
              <p className="mt-1 text-xs text-blue-700">
                Current filter:{" "}
                {fileType === "all" ? "images, videos, documents" : fileType}
              </p>
            </div>

            {files.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Selected Files
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {files.map((file, idx) => {
                    const preview = previews[idx];
                    const isImage = file.type.startsWith("image/");
                    const isVideo = file.type.startsWith("video/");

                    return (
                      <div
                        key={`${file.name}-${idx}`}
                        className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="h-28 bg-slate-100">
                          {isImage && preview ? (
                            <img
                              src={preview}
                              alt={file.name}
                              className="h-full w-full object-cover"
                            />
                          ) : isVideo && preview ? (
                            <video
                              src={preview}
                              controls
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-xs font-semibold uppercase tracking-widest text-slate-500">
                              Document
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="truncate text-xs font-semibold text-slate-700">
                            {file.name}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {Math.max(file.size / (1024 * 1024), 0.01).toFixed(
                              2,
                            )}{" "}
                            MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(idx);
                          }}
                          className="absolute right-2 top-2 h-7 w-7 rounded-full bg-slate-900/80 text-xs font-bold text-white transition hover:bg-rose-600"
                          title="Remove"
                        >
                          X
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white shadow-xl transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Saving Event..." : "Publish Event"}
            </button>
          </section>

          <aside className="space-y-6 lg:col-span-4">
            <div className="rounded-3xl border border-white/25 bg-white/10 p-4 text-white backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-cyan-100">
                Upload Snapshot
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-white/15 p-3 text-center">
                  <p className="text-lg font-bold">{totalUploads}</p>
                  <p className="text-xs text-slate-200">Total</p>
                </div>
                <div className="rounded-xl bg-white/15 p-3 text-center">
                  <p className="text-lg font-bold">{completedUploads}</p>
                  <p className="text-xs text-slate-200">Done</p>
                </div>
                <div className="rounded-xl bg-white/15 p-3 text-center">
                  <p className="text-lg font-bold">{failedUploads}</p>
                  <p className="text-xs text-slate-200">Failed</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/25 bg-slate-900/40 p-4 text-white backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-cyan-100">
                Firebase Progress
              </p>
              {Object.keys(uploadProgress).length > 0 ? (
                <div className="mt-3 space-y-3">
                  {files.map((file) => {
                    const current = uploadProgress[file.name];
                    const progressValue =
                      current === "error" ? 100 : current || 0;

                    return (
                      <div key={file.name}>
                        <div className="mb-1 flex items-center justify-between">
                          <span className="max-w-[70%] truncate text-xs text-slate-100">
                            {file.name}
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              current === "error"
                                ? "text-rose-300"
                                : "text-cyan-200"
                            }`}
                          >
                            {current === "error"
                              ? "Error"
                              : `${progressValue}%`}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-white/20">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              current === "error"
                                ? "bg-rose-400"
                                : "bg-cyan-300"
                            }`}
                            style={{ width: `${progressValue}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-300">
                  Progress bars appear after you start upload.
                </p>
              )}
            </div>

            {/* <div className="rounded-3xl border border-white/25 bg-slate-950/50 p-4 text-white backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-cyan-100">
                Service Logs
              </p>
              <div className="mt-3 space-y-4">
                <div>
                  <p className="mb-1 text-xs font-semibold text-slate-200">
                    Firebase
                  </p>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-2">
                    {uploadLogs.firebase.length === 0 ? (
                      <p className="text-xs text-slate-400">No logs yet</p>
                    ) : (
                      uploadLogs.firebase.map((log) => (
                        <p key={log.id} className="text-xs text-slate-200">
                          [{log.time}] {log.fileName}:{" "}
                          <span className={getLogStatusClass(log.status)}>
                            {log.status}
                          </span>{" "}
                          - {log.message}
                        </p>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-xs font-semibold text-slate-200">
                    Immich
                  </p>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-2">
                    {uploadLogs.immich.length === 0 ? (
                      <p className="text-xs text-slate-400">No logs yet</p>
                    ) : (
                      uploadLogs.immich.map((log) => (
                        <p
                          key={log.id}
                          className="whitespace-pre-wrap break-all text-xs text-slate-200"
                        >
                          [{log.time}] {log.fileName}:{" "}
                          <span className={getLogStatusClass(log.status)}>
                            {log.status}
                          </span>{" "}
                          - {log.message}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div> */}
            
          </aside>
        </div>
      </form>
    </div>
  );
}

export default AddEventForm;
